import { useState, useEffect } from "react";
import api from "../../lib/api";
import { formatDateTime } from "../../lib/timezone";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import {
  Phone,
  AlertCircle,
  CheckCircle2,
  Plus,
  Edit2,
  MessageSquare,
  Calendar as CalendarIcon,
  User,
  X,
  Trash2,
} from "lucide-react";

const PRIORITY_COLORS = {
  High: "bg-red-500/15 text-red-500 border-red-500/20",
  Medium: "bg-amber-500/15 text-amber-500 border-amber-500/20",
  Low: "bg-blue-500/15 text-blue-500 border-blue-500/20",
};

const STATUS_COLORS = {
  "Not reachable": "bg-orange-500/15 text-orange-500 border-orange-500/20",
  RNR: "bg-yellow-500/15 text-yellow-500 border-yellow-500/20",
  "Need followup": "bg-purple-500/15 text-purple-500 border-purple-500/20",
  New: "bg-blue-500/15 text-blue-500 border-blue-500/20",
  Interested: "bg-green-500/15 text-green-500 border-green-500/20",
  "Not interested": "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  Converted: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
  Lost: "bg-red-500/15 text-red-500 border-red-500/20",
};

const formatTime12hr = (timeStr) => {
  if (!timeStr) return "";
  const [hoursStr, minutesStr] = timeStr.split(":");
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr || "00";
  if (isNaN(hours)) return timeStr;
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  return `${hours}:${minutes} ${ampm}`;
};

export default function LeadDetailsModal({
  open,
  onOpenChange,
  lead,
  orgTimezone,
  onSuccess,
}) {
  const [followupResponse, setFollowupResponse] = useState("");
  const [localComments, setLocalComments] = useState([]);

  useEffect(() => {
    if (lead) {
      setLocalComments(lead.comments || []);
    }
  }, [lead]);

  const handleDeleteComment = async (createdAt) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;
    try {
      await api.delete(`/lead-management/${lead.id}/notes/${createdAt}`);
      toast.success("Comment deleted");
      setLocalComments((prev) => prev.filter((c) => c.createdAt !== createdAt));
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete comment");
    }
  };
  const [followupNotes, setFollowupNotes] = useState("");
  const [nextFollowupDate, setNextFollowupDate] = useState("");
  const [nextFollowupTime, setNextFollowupTime] = useState("10:00");
  const [expectedDate, setExpectedDate] = useState("");
  const [expectedTime, setExpectedTime] = useState("10:00");

  const [loading, setLoading] = useState(false);

  const renderActivityDetails = (details) => {
    if (!details) return null;
    return details.split("\n").map((line, idx) => {
      if (line.startsWith("Response Note:") || line.startsWith("Comment:")) {
        const isComment = line.startsWith("Comment:");
        const noteText = line
          .replace(isComment ? "Comment:" : "Response Note:", "")
          .trim();
        return (
          <div
            key={idx}
            className="mt-1.5 p-2 bg-amber-500/10 border border-amber-500/20 rounded-md text-foreground"
          >
            <span className="text-amber-600 dark:text-amber-400 font-bold text-[11px] uppercase tracking-wider">
              {isComment ? "Comment:" : "Response Note:"}
            </span>{" "}
            <span className="font-semibold text-foreground text-xs leading-normal">
              {noteText}
            </span>
          </div>
        );
      }
      return <p key={idx}>{line}</p>;
    });
  };

  useEffect(() => {
    if (lead) {
      setFollowupResponse("");
      setFollowupNotes("");
      setNextFollowupDate("");
      setNextFollowupTime("10:00");
      setExpectedDate(lead.expectedJoiningDate || "");
      setExpectedTime(lead.expectedJoiningTime || "10:00");
    }
  }, [lead, open]);

  if (!lead) return null;

  const handleFollowupStatusChange = async () => {
    if (!followupResponse) {
      toast.error("Please select a response");
      return;
    }
    if (
      ["Not reachable", "RNR", "Need followup", "Interested"].includes(
        followupResponse,
      )
    ) {
      if (!nextFollowupDate) {
        toast.error("Next schedule date is required");
        return;
      }
      if (!nextFollowupTime) {
        toast.error("Next schedule time is required");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        status: "Closed",
        notes: followupNotes,
        response: followupResponse,
      };

      if (
        ["Not reachable", "RNR", "Need followup", "Interested"].includes(
          followupResponse,
        )
      ) {
        payload.nextScheduledDate = nextFollowupDate;
        payload.nextScheduledTime = nextFollowupTime;
      }

      if (["Need followup", "Interested"].includes(followupResponse)) {
        payload.expectedJoiningDate = expectedDate;
        payload.expectedJoiningTime = expectedTime;
      }

      await api.put(
        `/lead-management/followups/${lead.currentFollowup.id}`,
        payload,
      );
      toast.success("Follow-up response updated successfully");
      setFollowupResponse("");
      setFollowupNotes("");
      setNextFollowupDate("");
      setNextFollowupTime("10:00");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to update followup response",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] sm:max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden rounded-xl">
        <DialogHeader className="px-4 py-3 sm:px-6 sm:pt-5 sm:pb-3 border-b border-border shrink-0">
          <DialogTitle className="font-heading uppercase tracking-wide text-sm sm:text-base">
            Lead Details
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 space-y-4 sm:space-y-6">
          {/* Main Info */}
          <div className="p-3 sm:p-4 bg-secondary/20 rounded-xl space-y-2.5 sm:space-y-3 border border-border/40">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2.5">
              <div>
                <p className="font-heading font-bold text-base sm:text-lg leading-tight">
                  {lead.name}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    {lead.phone}
                  </span>
                  {lead.email && (
                    <span className="truncate max-w-[200px] sm:max-w-none">
                      {lead.email}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <Badge
                  className={`${PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS.Medium} border text-[9px] sm:text-[10px] font-mono uppercase px-1.5 py-0`}
                >
                  {lead.priority}
                </Badge>
                <Badge
                  className={`${STATUS_COLORS[lead.status] || STATUS_COLORS[lead.stage] || ""} border text-[9px] sm:text-[10px] font-mono uppercase px-1.5 py-0`}
                >
                  {lead.status || lead.stage}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5 sm:gap-4 text-xs sm:text-sm pt-2.5 border-t border-border/50">
              <div>
                <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Source
                </p>
                <p className="font-medium">{lead.source || "—"}</p>
              </div>
              {lead.interest && (
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Interest
                  </p>
                  <p className="font-medium">{lead.interest}</p>
                </div>
              )}
              {lead.reference && (
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Reference
                  </p>
                  <p className="font-medium">{lead.reference}</p>
                </div>
              )}
              {lead.groupNames?.length > 0 && (
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Groups
                  </p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {lead.groupNames.map((name, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="text-[9px] px-1 py-0"
                      >
                        {name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {lead.assignedStaffNames?.length > 0 && (
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Assigned Staff
                  </p>
                  <p className="font-medium">
                    {lead.assignedStaffNames.join(", ")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Follow-up Response Section */}
          {lead.currentFollowup && lead.currentFollowup.status !== "Closed" && (
            <div className="space-y-2.5 p-3 sm:p-4 border border-border rounded-xl bg-card">
              <div>
                <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Follow-up Response
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  For followup scheduled on:{" "}
                  <span className="font-semibold">
                    {lead.currentFollowup.scheduledDate}
                  </span>
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">
                    Select Response *
                  </Label>
                  <Select
                    value={followupResponse}
                    onValueChange={(val) => {
                      setFollowupResponse(val);
                      // Clear next date/time when response changes
                      setNextFollowupDate("");
                      setNextFollowupTime("10:00");
                      if (["Need followup", "Interested"].includes(val)) {
                        // Prefill expected date/time with lead's current values
                        setExpectedDate(lead.expectedJoiningDate || "");
                        setExpectedTime(lead.expectedJoiningTime || "10:00");
                        // Auto-fill next date/time if expected values are present
                        if (lead.expectedJoiningDate) {
                          setNextFollowupDate(lead.expectedJoiningDate);
                          setNextFollowupTime(
                            lead.expectedJoiningTime || "10:00",
                          );
                        }
                      }
                    }}
                  >
                    <SelectTrigger
                      className="h-9 text-xs sm:text-sm"
                      data-testid="modal-followup-response-select"
                    >
                      <SelectValue placeholder="Select response" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Not reachable">
                        Not reachable
                      </SelectItem>
                      <SelectItem value="RNR">RNR</SelectItem>
                      <SelectItem value="Need followup">
                        Need followup
                      </SelectItem>
                      <SelectItem value="Interested">Interested</SelectItem>
                      <SelectItem value="Not interested">
                        Not interested
                      </SelectItem>
                      <SelectItem value="Converted">Converted</SelectItem>
                      <SelectItem value="Lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-foreground">
                    Comment
                  </Label>
                  <Textarea
                    value={followupNotes}
                    onChange={(e) => setFollowupNotes(e.target.value)}
                    placeholder="Enter comment here..."
                    className="text-xs sm:text-sm border-amber-500/30 bg-amber-500/[0.02] focus-visible:border-amber-500 focus-visible:ring-amber-500/20 placeholder:text-muted-foreground/50"
                    rows={3}
                    data-testid="modal-followup-notes"
                  />
                </div>

                {/* Conditional inputs based on response */}
                {["Not reachable", "RNR"].includes(followupResponse) && (
                  <div className="p-3 bg-orange-500/5 border border-orange-500/10 rounded-lg space-y-2.5">
                    <div className="flex items-center gap-1.5 text-orange-500">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-xs font-medium">
                        Schedule Next Follow-up
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">Next Date *</Label>
                        <Input
                          type="date"
                          value={nextFollowupDate}
                          onChange={(e) => setNextFollowupDate(e.target.value)}
                          className="h-8 text-xs px-2 mt-0.5"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Next Time *</Label>
                        <Input
                          type="time"
                          value={nextFollowupTime}
                          onChange={(e) => setNextFollowupTime(e.target.value)}
                          className="h-8 text-xs px-2 mt-0.5"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {["Need followup", "Interested"].includes(followupResponse) && (
                  <div className="p-3 bg-purple-500/5 border border-purple-500/10 rounded-lg space-y-2.5">
                    <div className="flex items-center gap-1.5 text-purple-500">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-xs font-medium">
                        Expected Date & Time of Join
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">Expected Date *</Label>
                        <Input
                          type="date"
                          value={expectedDate}
                          onChange={(e) => {
                            setExpectedDate(e.target.value);
                            setNextFollowupDate(e.target.value);
                          }}
                          className="h-8 text-xs px-2 mt-0.5"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Expected Time *</Label>
                        <Input
                          type="time"
                          value={expectedTime}
                          onChange={(e) => {
                            setExpectedTime(e.target.value);
                            setNextFollowupTime(e.target.value);
                          }}
                          className="h-8 text-xs px-2 mt-0.5"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-1.5 pt-1">
                  <Button
                    size="sm"
                    onClick={handleFollowupStatusChange}
                    disabled={loading}
                    className="text-xs font-heading uppercase tracking-wider"
                    data-testid="modal-confirm-followup-status-btn"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                    Submit Response
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Read-only Follow-up Response Details Section */}
          {lead.currentFollowup && lead.currentFollowup.status === "Closed" && (
            <div className="space-y-3 p-3.5 sm:p-4 border border-emerald-500/20 rounded-xl bg-emerald-500/[0.02] relative overflow-hidden backdrop-blur-sm">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-xl pointer-events-none" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-emerald-500 font-bold">
                    Follow-up Response (Closed)
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Scheduled for:{" "}
                    <span className="font-semibold text-foreground/80">
                      {lead.currentFollowup.scheduledDate}
                      {lead.currentFollowup.scheduledTime && ` at ${formatTime12hr(lead.currentFollowup.scheduledTime)}`}
                    </span>
                  </p>
                </div>
                <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/20 text-[9px] font-mono uppercase px-2 py-0.5">
                  Completed
                </Badge>
              </div>

              <div className="space-y-2.5 pt-1 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs font-mono uppercase">Outcome:</span>
                  <Badge className={`${STATUS_COLORS[lead.currentFollowup.response] || STATUS_COLORS.New || ""} border text-[10px] font-mono uppercase px-1.5 py-0`}>
                    {lead.currentFollowup.response || "No Response"}
                  </Badge>
                </div>

                {lead.currentFollowup.notes && (
                  <div className="space-y-1">
                    <span className="text-muted-foreground text-xs font-mono uppercase">Response Notes:</span>
                    <p className="p-3 bg-secondary/30 rounded-lg text-foreground whitespace-pre-wrap leading-normal border border-border/40 text-xs sm:text-sm shadow-inner">
                      {lead.currentFollowup.notes}
                    </p>
                  </div>
                )}

                {lead.currentFollowup.updatedAt && (
                  <p className="text-[9px] font-mono text-muted-foreground text-right mt-1 select-none">
                    Recorded on {formatDateTime(lead.currentFollowup.updatedAt, orgTimezone)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Activity History */}
          <div className="space-y-2">
            <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Activity History
            </p>
            {lead.activities?.length > 0 ? (
              <div className="divide-y divide-border/30 border border-border/30 rounded-lg overflow-hidden bg-secondary/5">
                {lead.activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex flex-col gap-1 px-3 py-2 hover:bg-secondary/15 transition-colors text-xs"
                  >
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="shrink-0 text-muted-foreground">
                          {activity.action === "created" && (
                            <Plus className="h-3.5 w-3.5 text-primary" />
                          )}
                          {activity.action === "updated" && (
                            <Edit2 className="h-3 w-3 text-blue-500" />
                          )}
                          {activity.action === "status_changed" && (
                            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          )}
                          {activity.action === "note_added" && (
                            <MessageSquare className="h-3 w-3 text-amber-500" />
                          )}
                          {activity.action === "followup_scheduled" && (
                            <CalendarIcon className="h-3 w-3 text-purple-500" />
                          )}
                          {activity.action === "followup_completed" && (
                            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          )}
                          {activity.action === "converted" && (
                            <User className="h-3 w-3 text-emerald-500" />
                          )}
                          {activity.action === "deleted" && (
                            <X className="h-3 w-3 text-red-500" />
                          )}
                        </div>
                        <span className="font-heading uppercase text-[9px] tracking-wider font-bold shrink-0 text-foreground">
                          {activity.action.replace(/_/g, " ")}
                        </span>
                        <span className="text-muted-foreground text-[10px] truncate">
                          by {activity.userName}
                        </span>
                      </div>
                      <span className="text-[9px] font-mono text-muted-foreground shrink-0 select-none">
                        {formatDateTime(activity.createdAt, orgTimezone)}
                      </span>
                    </div>
                    <div className="pl-5 text-xs text-foreground/80 leading-normal whitespace-pre-wrap break-words">
                      {renderActivityDetails(activity.details)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-muted-foreground text-center py-4">
                No activity recorded
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="px-4 py-3 sm:px-6 sm:py-4 border-t border-border shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto text-xs font-heading uppercase tracking-wider"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
